export interface NormalizedEbayFunctionError {
	message: string;
	code?: string;
	categoryId?: string;
	missing?: unknown[];
}

const extractText = (value: unknown): string | null => {
	if (value == null) return null;
	if (typeof value === 'string') return value;
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	if (value instanceof Error) return value.message;

	if (Array.isArray(value)) {
		const parts = value.map(extractText).filter((part): part is string => Boolean(part));
		return parts.length ? parts.join('; ') : null;
	}

	if (typeof value === 'object') {
		const obj = value as Record<string, unknown>;
		for (const key of ['longMessage', 'message', 'errorMessage', 'description', 'detail', 'name', 'code', 'errorId']) {
			const text = extractText(obj[key]);
			if (text) return text;
		}

		if (obj.errors) {
			const text = extractText(obj.errors);
			if (text) return text;
		}

		try {
			const json = JSON.stringify(value);
			return json && json !== '{}' ? json : null;
		} catch {
			return null;
		}
	}

	return null;
};

const missingAspectNames = (missing: unknown): string[] => {
	if (!Array.isArray(missing)) return [];
	return missing
		.map((entry) => {
			if (typeof entry === 'string') return entry;
			if (entry && typeof entry === 'object') {
				const name = (entry as { name?: unknown }).name;
				return typeof name === 'string' ? name : null;
			}
			return null;
		})
		.filter((name): name is string => Boolean(name));
};

export const normalizeEbayFunctionError = (body: unknown, fallback: string): NormalizedEbayFunctionError => {
	if (!body || typeof body !== 'object') {
		return { message: extractText(body) || fallback };
	}

	const obj = body as Record<string, unknown>;
	const rawError = obj.error;
	const code = typeof rawError === 'string'
		? rawError
		: typeof obj.code === 'string'
			? obj.code
			: undefined;
	const missing = Array.isArray(obj.missing) ? obj.missing : undefined;
	const categoryId = typeof obj.category_id === 'string' ? obj.category_id : undefined;

	if (code === 'aspects_missing') {
		const names = missingAspectNames(missing);
		return {
			message: names.length ? `Missing eBay fields: ${names.join(', ')}` : 'Missing required eBay fields',
			code,
			categoryId,
			missing,
		};
	}

	const message = extractText(obj.message) || extractText(rawError) || extractText(obj.errors) || fallback;
	return { message, code, categoryId, missing };
};